import { TimerManager } from '@examflow/core';
import { cn } from '@examflow/ui';
import { Pause, Play, RotateCcw } from 'lucide-react';
import {
  resolveCountdownProgress,
  resolveCountdownUrgency,
  resolveSoftTargetPhase,
  resolveSoftTargetProgress,
  softTargetStatusLabel,
  type CountdownUrgency,
  type SoftTargetPhase,
} from './sessionClockModel';

export type SessionClockVariant = 'countdown' | 'elapsed' | 'softTarget';

export type SessionClockProps = {
  variant: SessionClockVariant;
  /** Countdown: remaining ms. Elapsed/softTarget: elapsed ms. */
  ms: number;
  /** Mock total budget for remaining ring. */
  totalMs?: number | null;
  /** Writing suggested duration (soft). */
  targetMs?: number | null;
  /**
   * Training / writing only — never wire for Mock countdown.
   * Pause freezes the count-up; reset clears elapsed to 0.
   */
  paused?: boolean;
  onTogglePause?: () => void;
  onReset?: () => void;
  className?: string;
};

const RING_SIZE = 44;
const RING_STROKE = 3.5;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function urgencyShellClass(urgency: CountdownUrgency): string {
  switch (urgency) {
    case 'normal':
      return 'bg-slate-100 text-slate-800 ring-1 ring-slate-200/80';
    case 'notice':
      return 'bg-amber-50 text-amber-900 ring-1 ring-amber-200';
    case 'urgent':
      return 'bg-error-50 font-semibold text-error-700 ring-1 ring-error-200';
    case 'critical':
      return 'bg-error-100 font-semibold text-error-800 ring-2 ring-error-300';
    default: {
      const _exhaustive: never = urgency;
      return _exhaustive;
    }
  }
}

function softShellClass(phase: SoftTargetPhase | null, paused: boolean): string {
  if (paused) {
    return 'bg-slate-50 text-slate-700 ring-1 ring-slate-300';
  }
  switch (phase) {
    case null:
    case 'under':
      return 'bg-slate-100 text-slate-800 ring-1 ring-slate-200/80';
    case 'near':
      return 'bg-amber-50 text-amber-900 ring-1 ring-amber-200';
    case 'over':
      return 'bg-warning-50 font-semibold text-warning-800 ring-1 ring-warning-300';
    default: {
      const _exhaustive: never = phase;
      return _exhaustive;
    }
  }
}

function ringStrokeClass(
  kind: 'countdown' | 'soft' | 'elapsed',
  tone: CountdownUrgency | SoftTargetPhase | null
): string {
  if (kind === 'countdown') {
    switch (tone as CountdownUrgency) {
      case 'notice':
        return 'stroke-amber-500';
      case 'urgent':
        return 'stroke-error-500';
      case 'critical':
        return 'stroke-error-700';
      case 'normal':
      default:
        return 'stroke-slate-700';
    }
  }
  if (kind === 'elapsed') {
    return 'stroke-slate-500';
  }
  switch (tone as SoftTargetPhase | null) {
    case 'near':
      return 'stroke-amber-500';
    case 'over':
      return 'stroke-warning-600';
    case 'under':
    case null:
    default:
      return 'stroke-primary-600';
  }
}

/**
 * Progress ring with optional play/pause seated in the hollow center.
 * Mock countdown never passes onTogglePause.
 */
function ProgressRing({
  progress,
  toneClass,
  paused = false,
  onTogglePause,
}: {
  /** 0–1 fill; null = track only. */
  progress: number | null;
  toneClass: string;
  paused?: boolean;
  onTogglePause?: () => void;
}) {
  const fill = progress == null ? 0 : Math.min(1, Math.max(0, progress));
  const offset = RING_CIRCUMFERENCE * (1 - fill);
  const showFill = progress != null;

  return (
    <span
      className="relative inline-flex shrink-0 items-center justify-center"
      style={{ width: RING_SIZE, height: RING_SIZE }}
    >
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        className="pointer-events-none absolute inset-0 -rotate-90"
        aria-hidden
      >
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          strokeWidth={RING_STROKE}
          className="stroke-slate-200/90"
        />
        {showFill ? (
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={offset}
            className={cn('transition-[stroke-dashoffset] duration-500', toneClass)}
          />
        ) : null}
      </svg>
      {onTogglePause ? (
        <button
          type="button"
          onClick={onTogglePause}
          className="relative z-10 inline-flex h-7 w-7 items-center justify-center rounded-full text-current/85 transition-colors hover:bg-black/5 hover:text-current focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-1"
          aria-label={paused ? '继续计时' : '暂停计时'}
          data-testid="session-clock-pause"
        >
          {paused ? (
            <Play className="h-3.5 w-3.5 translate-x-px" />
          ) : (
            <Pause className="h-3.5 w-3.5" />
          )}
        </button>
      ) : null}
    </span>
  );
}

function ResetControl({ onReset }: { onReset?: () => void }) {
  if (!onReset) return null;
  return (
    <button
      type="button"
      onClick={onReset}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-current/80 transition-colors hover:bg-black/5 hover:text-current focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-1"
      aria-label="复位计时"
      data-testid="session-clock-reset"
    >
      <RotateCcw className="h-3.5 w-3.5" />
    </button>
  );
}

/**
 * Shared Quiz session clock chrome.
 * countdown = Mock hard remaining (no pause/reset);
 * elapsed / softTarget = Training count-up (pause in ring center; reset aside).
 */
export function SessionClock({
  variant,
  ms,
  totalMs = null,
  targetMs = null,
  paused = false,
  onTogglePause,
  onReset,
  className,
}: SessionClockProps) {
  const displayMs = Math.max(0, ms);
  const timeLabel = TimerManager.formatTime(displayMs);

  if (variant === 'countdown') {
    const urgency = resolveCountdownUrgency(displayMs);
    const progress = resolveCountdownProgress(displayMs, totalMs);
    return (
      <span
        className={cn(
          'inline-flex items-center gap-2.5 rounded-xl px-3 py-2',
          urgencyShellClass(urgency),
          className
        )}
        data-testid="session-clock-countdown"
        data-urgency={urgency}
        role="timer"
        aria-label={`剩余 ${timeLabel}`}
      >
        <ProgressRing
          progress={progress}
          toneClass={ringStrokeClass('countdown', urgency)}
        />
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-70">
            剩余
          </span>
          <span className="font-mono text-base tabular-nums sm:text-lg">{timeLabel}</span>
        </span>
      </span>
    );
  }

  if (variant === 'softTarget') {
    const phase = resolveSoftTargetPhase(displayMs, targetMs);
    const progress = resolveSoftTargetProgress(displayMs, targetMs);
    const status = softTargetStatusLabel(phase);
    return (
      <span
        className={cn(
          'inline-flex items-center gap-2.5 rounded-xl px-3 py-2',
          softShellClass(phase, paused),
          className
        )}
        data-testid="session-clock-soft-target"
        data-phase={phase ?? 'none'}
        data-paused={paused ? 'true' : 'false'}
        role="timer"
        aria-label={status ? `已用 ${timeLabel}，${status}` : `已用 ${timeLabel}`}
      >
        {(progress != null || onTogglePause) ? (
          <ProgressRing
            progress={progress}
            toneClass={ringStrokeClass('soft', phase)}
            paused={paused}
            onTogglePause={onTogglePause}
          />
        ) : null}
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-70">
            已用
          </span>
          <span className="font-mono text-base tabular-nums sm:text-lg">{timeLabel}</span>
          {status ? <span className="text-[11px] font-medium">{status}</span> : null}
        </span>
        <ResetControl onReset={onReset} />
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-2.5 rounded-xl px-3 py-2 text-slate-800 ring-1',
        paused ? 'bg-slate-50 ring-slate-300' : 'bg-slate-100 ring-slate-200/80',
        className
      )}
      data-testid="session-clock-elapsed"
      data-paused={paused ? 'true' : 'false'}
      role="timer"
      aria-label={`已练 ${timeLabel}`}
    >
      {onTogglePause ? (
        <ProgressRing
          progress={null}
          toneClass={ringStrokeClass('elapsed', null)}
          paused={paused}
          onTogglePause={onTogglePause}
        />
      ) : null}
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          已练
        </span>
        <span className="font-mono text-base tabular-nums text-slate-800 sm:text-lg">
          {timeLabel}
        </span>
      </span>
      <ResetControl onReset={onReset} />
    </span>
  );
}
